import { FC, useEffect, useState, useMemo } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import {
  reimportArticlesFromCAState,
  IReimportArticlesFromCA,
} from '../states/reimportArticlesFromCA.state.ts';
import { stepperState } from '../../../../../../../../../shared/components/stepper/states/stepper.state.ts';
import { Table } from '../../../../../../../../../shared/components/table/table.component.tsx';
import {
  Column,
  Footer,
} from '../../../../../../../../../shared/components/table/components/common.components.tsx';
import { useTranslation } from 'react-i18next';
import { Theme } from '../../../../../../../../../shared/styles/theme/theme.ts';
import { Content } from '../../../../../../../../../shared/components/content/content.component.tsx';
import { MessageRotator } from '../../../../../../../../../shared/components/messageRotator/messageRotator.component.tsx';

export const FirstStep: FC<IFirstStep> = () => {
  const { t } = useTranslation();
  const [listArticleNumberDuplicatesFromCA, setListArticleNumberDuplicatesFromCA] = useState<
    IReimportArticlesFromCA['listArticleNumberDuplicatesFromCA']
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const reimportArticlesFromCAStateSub = reimportArticlesFromCAState.sub
      .state()
      .subscribe(({ listArticleNumberDuplicatesFromCA }) => {
        if (listArticleNumberDuplicatesFromCA.length) {
          setListArticleNumberDuplicatesFromCA(listArticleNumberDuplicatesFromCA);
          setLoading(false);
        } else {
          stepperState.pub.nextStep({ activeStep: 0, valid: true });
        }
      });
    const loadingSub = reimportArticlesFromCAState.sub.firstPageLoading().subscribe(setLoading);
    return () => {
      reimportArticlesFromCAStateSub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  const messageRotator = useMemo(() => {
    const messagesForLongLoading = [
      t('settings.searching_for_duplicate_articles'),
      t('settings.scanning_articles_in_cashassist_and_wawiassist'),
      t('settings.comparing_data_between_systems'),
      t('settings.checking_article_uniqueness'),
      t('settings.finalizing_duplicate_search'),
    ];
    return <MessageRotator uniqueMessages={messagesForLongLoading} />;
  }, []);

  return (
    <Stack
      alignItems='center'
      height='calc(100vh - 177px)'
      justifyContent='center'
      sx={{
        overflowY: 'scroll',
      }}
    >
      <Content type='box' boxProps={{ width: 550 }} {...{ loading, messageRotator }}>
        <Box sx={{ ...(loading && { opacity: '0' }) }}>
          <Typography textAlign='center' fontSize={24} fontWeight={500}>
            {t('settings.duplicates_were_found')}
          </Typography>
          <Typography
            textAlign='center'
            fontSize={16}
            color={Theme.palette.text.secondary}
            fontWeight={500}
            mb={2}
            mt='10px'
          >
            {t('settings.message_to_fix_article_numbers')}
          </Typography>
          <Table
            data={listArticleNumberDuplicatesFromCA as []}
            replacedId='caId'
            heightOffset={225}
          >
            <Column
              field='caArticleNo'
              headerName={t('settings.ca_number')}
              width={170}
              sortable={false}
            />
            <Column
              field='caDescription'
              headerName={t('settings.ca_description')}
              width={210}
              flex={1}
              sortable={false}
            />
            <Footer
              numberRows={listArticleNumberDuplicatesFromCA.length}
              titleRows={t('article.articles', { count: listArticleNumberDuplicatesFromCA.length })}
              onRefresh={() => {
                reimportArticlesFromCAState.pub.recheckDuplicatesFromCA();
                setLoading(true);
              }}
            />
          </Table>
        </Box>
      </Content>
    </Stack>
  );
};

interface IFirstStep {}
